import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";

const monteCarloMasters: ChauffeurPage = {
  slug: "monte-carlo-masters",
  title: "Private chauffeur Monte-Carlo Masters – VIP tennis VTC",
  subtitle:
    "Private chauffeur during the Rolex Monte-Carlo Masters: club ↔ hotel ↔ Monaco links, sponsor hospitality shuttles and Country Club access.",
  keywords: [
    "monte-carlo masters chauffeur",
    "monte-carlo masters private chauffeur",
    "rolex monte-carlo masters chauffeur",
  ],
  hero: {
    title: "Private chauffeur during the Rolex Monte-Carlo Masters",
    subtitle:
      "In mid-April, the clay-court Masters 1000 plays at the Monte-Carlo Country Club, set on the hillside: a chauffeur for players, sponsors and VIP guests.",
    keywords: [
      "Monte-Carlo Masters chauffeur",
      "Monte-Carlo Country Club VTC",
      "Monaco tennis chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Rolex Monte-Carlo Masters",
    badges: [
      "Club ↔ hotel ↔ Monaco",
      "Hospitality shuttles",
      "Country Club access",
    ],
  },
  benefits: {
    heading: "Why a chauffeur during the Masters",
    items: [
      {
        title: "Hillside Country Club",
        description:
          "The Monte-Carlo Country Club sits in Roquebrune-Cap-Martin, up on the heights: a winding access road and a drop-off zone that saturates fast, which the chauffeur anticipates.",
      },
      {
        title: "Club ↔ hotel ↔ Monaco",
        description:
          "Players and guests mostly stay in Monaco: scheduled links between the Principality, the hotels and the club entrance throughout the fortnight.",
      },
      {
        title: "Sponsor hospitality",
        description:
          "Boxes, lunches and partner evenings: coordinated shuttles for guest groups across the sessions.",
      },
      {
        title: "Nice airport",
        description:
          "Flight tracking and welcome for the arrivals of players, staff and sponsors in mid-April, with a direct transfer to Monaco or Cap-Martin.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during the Monte-Carlo Masters",
    services: [
      {
        title: "Nice airport transfer",
        text: "Nice ↔ Monaco / Roquebrune link with flight tracking for players, staff and sponsor guests.",
      },
      {
        title: "Club ↔ hotel link",
        text: "Scheduled round-trips between the Monaco hotels and the Monte-Carlo Country Club entrance, timed to the sessions.",
      },
      {
        title: "Sponsor hospitality shuttles",
        text: "Coordinated transport of guest groups to the boxes, lunches and partner receptions.",
      },
      {
        title: "Daily hire",
        text: "A dedicated chauffeur to chain sessions, meetings and movements around the Principality.",
      },
      {
        title: "Dinners & evenings",
        text: "Partner dinners and evenings in Monte-Carlo, with a guaranteed late-night return after the evening matches.",
      },
    ],
  },
  locations: {
    heading: "Key points during the Masters",
    items: [
      {
        name: "Monte-Carlo Country Club",
        description:
          "Clay courts in Roquebrune-Cap-Martin, just over the border, up on the heights.",
      },
      {
        name: "Monte-Carlo & Casino",
        description:
          "Palaces, sponsor boxes and prestige dinners where players and guests stay.",
      },
      {
        name: "Roquebrune-Cap-Martin",
        description:
          "Cap-Martin hotels and villas in the immediate vicinity of the club.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Terminals 1 and 2, arrivals concentrated in mid-April.",
      },
      {
        name: "Menton & Italian border",
        description: "Additional lodging east of the Principality.",
      },
    ],
  },
  generic: {
    heading: "The Rolex Monte-Carlo Masters from a chauffeur's seat",
    subheading:
      "In mid-April, the clay-court Masters 1000 mixes ATP players, sponsors and VIPs — on a hillside site that's hard to reach.",
    paragraphs: [
      "Every April, the Rolex Monte-Carlo Masters brings the ATP tennis elite to the Monte-Carlo Country Club. The club isn't in Monaco itself but in Roquebrune-Cap-Martin, just over the border, clinging to the heights: winding roads and a drop-off zone that fills quickly at session times. A chauffeur who knows the access drops you as close as possible and collects you at the right moment.",
      "Players, staff and sponsor guests mostly arrive via Nice Côte d'Azur airport. Flight tracking, welcome and a direct transfer to the Monaco or Cap-Martin hotels, ahead of the daily links to the club.",
      "The tournament is also a hospitality showcase: boxes, lunches and partner evenings punctuate the fortnight. Coordinated shuttles and hourly hire keep the same chauffeur for guest groups, with late-night returns after the evening matches.",
    ],
    bullets: [
      "Country Club access mastered",
      "Flight tracking in mid-April",
      "Sponsor hospitality shuttles",
    ],
  },
  process: {
    heading: "Booking a chauffeur for the Monte-Carlo Masters",
    steps: [
      "Tell us your dates, your hotel in Monaco or Cap-Martin and your session times.",
      "We plan the club ↔ hotel links and Country Club access around the tournament flow.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Hire and hospitality shuttles for sessions, boxes and partner dinners.",
    ],
  },
  faq: [
    {
      q: "How do I book a chauffeur during the Monte-Carlo Masters?",
      a: "Book ahead: in mid-April, Monaco and Cap-Martin are in high demand for the tournament. Share your dates, your hotel and your session times so we can secure a dedicated chauffeur for the fortnight.",
    },
    {
      q: "Is the Monte-Carlo Country Club hard to reach?",
      a: "The club is in Roquebrune-Cap-Martin, up on the heights just over the border, with a winding access road and a drop-off zone that fills fast at session times. The chauffeur anticipates the crowds, drops you as close as possible to the entrance and collects you at the planned slot.",
    },
    {
      q: "Do you handle hospitality shuttles for sponsors?",
      a: "Yes. We coordinate the transport of guest groups to the boxes, lunches and partner evenings, with scheduled links between the Monaco hotels and the club throughout the tournament.",
    },
    {
      q: "Do you handle transfers from Nice airport?",
      a: "Yes, with flight tracking and welcome. We handle the arrivals of players, staff and sponsors between Nice airport and Monaco or Cap-Martin, then the daily links to the Country Club.",
    },
  ],
  relatedLinks: [
    {
      label: "Monaco ⇄ Nice Airport, rate and duration",
      href: "/en/chauffeur/transfers/monaco-nice-airport",
    },
    {
      label: "Cannes ⇄ Monaco, rate and duration",
      href: "/en/chauffeur/transfers/cannes-monaco",
    },
    {
      label: "Book online",
      href: "/en/reservation",
    },
  ],
  cta: {
    title: "Your chauffeur for the Monte-Carlo Masters",
    text: "Club ↔ hotel ↔ Monaco links, hospitality shuttles and Country Club access: book a dedicated chauffeur for the tournament.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title:
      "Monte-Carlo Masters Chauffeur — VIP Country Club car service | KS Rent Services",
    description:
      "Private chauffeur during the Rolex Monte-Carlo Masters: club ↔ hotel ↔ Monaco links, sponsor hospitality shuttles, Country Club access and Nice airport runs.",
    canonical: "/en/chauffeur/monte-carlo-masters",
  },
};

export default monteCarloMasters;
